// ScoreTableInspector — renders the han/fu -> points table for the current RuleSet. The table is
// ALWAYS engine-computed (via the `scoreTable` RPC, which uses the same base-from-han/fu + payment
// matrix the scorer uses), so it can never drift from the board (spec §6.1 #3). Re-queries whenever
// `revision` changes (save→reflect). Shows the aotenjou (uncapped) note when enabled.
import { useEffect, useState } from 'react';
import type { ScoreTableCell } from '@mj/engine';
import { effectiveLimitMode } from '@mj/engine';
import { useStudioStore, useStudioApi } from '../useStore';

// capped caps at the kazoe ladder (13 han); stepped/aotenjou keep climbing, so show higher tiers too.
const HANS_CAPPED = [1, 2, 3, 4, 5, 6, 8, 11, 13];
const HANS_UNCAPPED = [1, 4, 5, 6, 8, 11, 13, 16, 19, 22, 26];
const FUS = [20, 25, 30, 40, 50, 60, 70];

export function ScoreTableInspector() {
  const api = useStudioApi();
  const revision = useStudioStore((s) => s.revision);
  const mode = useStudioStore((s) => effectiveLimitMode(s.ruleSet));
  const hans = mode === 'capped' ? HANS_CAPPED : HANS_UNCAPPED;
  const [table, setTable] = useState<ScoreTableCell[][] | null>(null);

  useEffect(() => {
    let alive = true;
    const st = api.getState();
    void st.engine.call('scoreTable', { rs: st.ruleSet, hans, fus: FUS }).then((t) => {
      if (alive) setTable(t);
    });
    return () => {
      alive = false;
    };
    // hans derives from `mode` (← ruleSet), and every rule edit bumps `revision`, so this covers it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, revision]);

  if (!table) return <div>computing…</div>;

  return (
    <div>
      <p style={{ fontSize: 12, color: '#666' }}>
        Non-dealer ron / (dealer ron). Engine-computed — always matches the board.
        {mode === 'aotenjou' && <strong style={{ color: '#a33' }}> 青天井: scores are uncapped.</strong>}
        {mode === 'stepped' && <strong style={{ color: '#a33' }}> 段階青天井: 満貫単位で無制限。</strong>}
      </p>
      <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={th}>han \ fu</th>
            {FUS.map((fu) => (
              <th key={fu} style={th}>
                {fu}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.map((row, ri) => (
            <tr key={hans[ri]}>
              <th style={th}>{hans[ri]}</th>
              {row.map((cell) => (
                <td key={cell.fu} style={td(cell.limit !== '')}>
                  {cell.limit && hans[ri]! >= 5 ? (
                    <span title={cell.limit}>
                      {cell.nonDealerRon.toLocaleString()} / {cell.dealerRon.toLocaleString()}
                      <br />
                      <span style={{ color: '#a33', fontSize: 10 }}>{cell.limit}</span>
                    </span>
                  ) : (
                    <span>
                      {cell.nonDealerRon.toLocaleString()} / {cell.dealerRon.toLocaleString()}
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const th: React.CSSProperties = { border: '1px solid #ccc', padding: '3px 8px', background: '#f4f4f4', textAlign: 'right' };
function td(limit: boolean): React.CSSProperties {
  return { border: '1px solid #ddd', padding: '3px 8px', textAlign: 'right', background: limit ? '#fff7f7' : '#fff' };
}
